import { useCampaignCode } from '@campaign-services/hooks/useCampaignCode'
import { Platform } from '@cart-services/hooks/fp-b2b/store-map'
import { useFpB2bCartService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-cart-service'
import { useWpCartService } from '@cart-services/hooks/useWpCartService'
import { type PartialCartItem } from '@cart-services/types'
import { type CountryCode } from '@constants'
import { useDialog } from '@dni-hooks/useDialog'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { Button } from '@pets-ui/Button/Button'
import { Dialog } from '@pets-ui/Dialog/Dialog'
import { DialogActions } from '@pets-ui/DialogActions/DialogActions'
import { DialogContent } from '@pets-ui/DialogContent/DialogContent'
import { DialogHeader } from '@pets-ui/DialogHeader/DialogHeader'
import { type SnackbarVariantsProps } from '@pets-ui/Snackbar/SnackbarVariants'
import { useSnackbar } from '@pets-ui/Snackbar/useSnackbar'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { wordPressNavigate } from '@utils'
import { Trans, useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom-v5'

export const DIALOG_ADD_TO_CART_ERROR = 'DIALOG_ADD_TO_CART_ERROR'
export interface DialogAddToCartErrorProps {
  environment?: 'wordPress' | 'merchant' | 'campaign'
  countryCode: CountryCode
  campaignId?: number
  items: PartialCartItem[]
}

const DialogAddToCartErrorBody = ({ handleNewItemsOnly, handleProceedToCheckout, onClose }: {
  handleNewItemsOnly: () => void
  handleProceedToCheckout: () => void
  onClose: () => void
}) => {
  const { t } = useTranslation()

  return (
    <>
      <DialogHeader align="center" title={t('wpOrder.checkoutSeparate', { defaultValue: '無法合併加車' })} onClose={onClose} />
      <DialogContent>
        <Text align="center" color={colors.grey800}>
          <Trans
            i18nKey="wpOrder.checkoutSeparateText"
            components={{
              Br: <div />,
            }}
          />
        </Text>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleNewItemsOnly}
          variant="outlined"
          color="grey"
          fullWidth
          size="large"
          whiteSpace="nowrap"
        >
          {t('wpOrder.newItemsOnly', { defaultValue: '清空並加入新商品' })}
        </Button>
        <Button
          onClick={handleProceedToCheckout}
          fullWidth
          size="large"
          whiteSpace="nowrap"
        >
          {t('wpOrder.proceedToCheckout', { defaultValue: '先結帳再購買' })}
        </Button>
      </DialogActions>
    </>
  )
}

const WpDialogAddToCartErrorComponent = () => {
  const { t } = useTranslation()

  const { closeDialog, countryCode, items } = useDialog<DialogAddToCartErrorProps>(DIALOG_ADD_TO_CART_ERROR)
  const { openSnackbar } = useSnackbar<SnackbarVariantsProps>()
  const { overwriteCartItems } = useWpCartService(countryCode)

  const showSnackbar = () => {
    openSnackbar({
      message: t('wpOrder.addedSuccessfully', { defaultValue: '訂單商品已加入購物車！' }),
      severity: 'success',
      action: (
        <Button
          variant="text"
          color="blue"
          onClick={() => wordPressNavigate('/cart/')}
        >
          {t('wpOrder.checkout', { defaultValue: '前往結帳' })}
        </Button>
      ),
    })
  }

  const handleNewItemsOnly = useCallbackEvent(() => {
    overwriteCartItems(items)
    closeDialog()
    showSnackbar()
  })

  const handleProceedToCheckout = useCallbackEvent(() => {
    wordPressNavigate('/cart/')
    closeDialog()
  })

  return (
    <DialogAddToCartErrorBody
      onClose={closeDialog}
      handleNewItemsOnly={handleNewItemsOnly}
      handleProceedToCheckout={handleProceedToCheckout}
    />
  )
}

const MerchantDialogAddToCartErrorComponent = () => {
  const { t } = useTranslation()

  const { closeDialog, campaignId, items, environment} = useDialog<DialogAddToCartErrorProps>(DIALOG_ADD_TO_CART_ERROR)
  const { overwriteCartItems } = useFpB2bCartService(campaignId ?? null, Platform.MERCHANT)
  const { openSnackbar } = useSnackbar<SnackbarVariantsProps>()

  const { campaignCodeFromUrl } = useCampaignCode()
  const history = useHistory()
  const isCampaignEnvironment = environment === 'campaign'

  const showSnackbar = () => {
    openSnackbar({
      message: t('wpOrder.addedSuccessfully', { defaultValue: '訂單商品已加入購物車！' }),
      severity: 'success',
      action: (
        <Button
          variant="text"
          color="blue"
          onClick={() => {
            if (isCampaignEnvironment) {
              history.push(`/campaigns/${campaignCodeFromUrl}/cart`)
            } else {
              history.push(`/merchant/${campaignCodeFromUrl}/cart`)
            }
          }}
        >
          {t('wpOrder.checkout', { defaultValue: '前往結帳' })}
        </Button>
      ),
    })
  }

  const handleNewItemsOnly = useCallbackEvent(() => {
    overwriteCartItems(items)
    closeDialog()
    showSnackbar()
  })

  const handleProceedToCheckout = useCallbackEvent(() => {
    if (isCampaignEnvironment) {
      history.push(`/campaigns/${campaignCodeFromUrl}/cart`)
    } else {
      history.push(`/merchant/${campaignCodeFromUrl}/cart`)
    }
    closeDialog()
  })

  return (
    <DialogAddToCartErrorBody
      onClose={closeDialog}
      handleNewItemsOnly={handleNewItemsOnly}
      handleProceedToCheckout={handleProceedToCheckout}
    />
  )
}

export const DialogAddToCartError = () => {
  const { open, closeDialog, environment } = useDialog<DialogAddToCartErrorProps>(DIALOG_ADD_TO_CART_ERROR)

  return (
    <Dialog onClose={closeDialog} open={open} maxWidth={320} fullWidth>
      {open && (environment === 'merchant' || environment === 'campaign'
        ? <MerchantDialogAddToCartErrorComponent />
        : <WpDialogAddToCartErrorComponent />)}
    </Dialog>
  )
}
